/**
 * One tab per conversation, plus a tab that starts a new one — and, for those
 * who may use it, "Ask Artemis" in front: the assistant, which answers first
 * and hands a chat to a person as a new conversation here.
 *
 * Intercom's tab bar carries spaces — Home, Messages, Help — because it has
 * several; ODI has exactly one kind of thing, so its tabs are the
 * conversations themselves. That only works because a conversation can be
 * named in a word or two: `label` is that name, written by a small model from
 * the opening message (core/logic/support/ticket_label.py) and renameable by
 * the reporter, and the ticket number stands in until it is set.
 *
 * Renaming is clicking the name — the open tab's label turns into the field,
 * with no pencil beside it. A pencil and a cross were there once and made the
 * selected tab wider than every other, so choosing a tab shoved the rest
 * along; a control that changes the layout it lives in costs more than the
 * discoverability it buys, and a name nobody discovers they can change is a
 * name that stays as it was. Ending a conversation left the strip entirely:
 * the cross sat inches from the panel's own cross and meant something else
 * entirely, so it is a question at the foot of the conversation instead.
 *
 * Plain MUI tabs rather than LinkTab: these select panel state, not a route.
 * The panel floats over whatever page the reporter is on and deliberately owns
 * no URL — putting it in one would also write `?support=…` into the page
 * address every capture records as its context.
 */
import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Badge from "@mui/material/Badge";
import InputBase from "@mui/material/InputBase";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import { SupportTicket } from "../../api/support-tickets";

/** The conversation's name, or its number until the label has been written.
 *  A word picked out of the first line used to stand in, and read as
 *  "Hej!" or "kolla" — two tickets named "kolla" side by side. */
export const shortLabel = (ticket: SupportTicket): string =>
	ticket.label.trim() || `#${ticket.id}`;

interface SupportConversationTabsProps {
	tickets: SupportTicket[];
	/** null while the new-message tab is selected. */
	activeId: number | null;
	hasUnseen: (ticket: SupportTicket) => boolean;
	onSelect: (ticket: SupportTicket) => void;
	onStartNew: () => void;
	/** Clicking the open tab's name renames it — there is nowhere else a
	 *  conversation's name belongs, and it is the whole gesture. */
	onRename: (ticket: SupportTicket, label: string) => void;
	/** How many conversations have aged out of the strip; 0 hides the tab. */
	olderCount: number;
	isBrowsingHistory: boolean;
	onShowHistory: () => void;
	/** "Ask Artemis" leads the strip: the assistant answers first, and a
	 *  person is the next tab or +. Staff-only while it is tried on prod. */
	showAssistant: boolean;
	isAssistantSelected: boolean;
	onSelectAssistant: () => void;
}

const NEW_MESSAGE_TAB = "new";
const ARCHIVE_TAB = "archive";
const ASSISTANT_TAB = "assistant";

const SupportConversationTabs: React.FC<SupportConversationTabsProps> = ({
	tickets,
	activeId,
	hasUnseen,
	onSelect,
	onStartNew,
	onRename,
	olderCount,
	isBrowsingHistory,
	onShowHistory,
	showAssistant,
	isAssistantSelected,
	onSelectAssistant,
}) => {
	const [editingId, setEditingId] = useState<number | null>(null);

	return (
		<Tabs
			value={
				isAssistantSelected
					? ASSISTANT_TAB
					: isBrowsingHistory
						? ARCHIVE_TAB
						: (activeId ?? NEW_MESSAGE_TAB)
			}
			onChange={(_event, next: number | string) => {
				setEditingId(null);
				if (next === ASSISTANT_TAB) {
					onSelectAssistant();
					return;
				}
				if (next === NEW_MESSAGE_TAB) {
					onStartNew();
					return;
				}
				if (next === ARCHIVE_TAB) {
					onShowHistory();
					return;
				}
				const chosen = tickets.find((ticket) => ticket.id === next);
				if (chosen) onSelect(chosen);
			}}
			variant="scrollable"
			scrollButtons="auto"
			sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
			data-component="support-conversation-tabs"
		>
			{showAssistant && (
				<Tab
					value={ASSISTANT_TAB}
					icon={<AutoAwesomeIcon fontSize="small" />}
					iconPosition="start"
					label="Ask Artemis"
					sx={{ minHeight: 40, textTransform: "none" }}
					data-component="support-tab-assistant"
				/>
			)}
			{tickets.map((ticket) => (
				<Tab
					key={ticket.id}
					value={ticket.id}
					sx={{ minHeight: 40, textTransform: "none" }}
					data-component="support-conversation-tab"
					data-ticket-id={ticket.id}
					// Selecting a tab goes through onChange; clicking the one
					// already selected fires no change at all, which is exactly
					// the gesture that opens its name for editing.
					onClick={() => {
						if (ticket.id === activeId) setEditingId(ticket.id);
					}}
					label={
						editingId === ticket.id ? (
							<InputBase
								autoFocus
								defaultValue={ticket.label}
								// Sized to the name it replaces, so editing does
								// not shove the tabs after it along either.
								inputProps={{
									maxLength: 24,
									size: Math.max(shortLabel(ticket).length, 6),
								}}
								sx={{ fontSize: "inherit" }}
								onBlur={(event) => {
									setEditingId(null);
									const next = event.target.value.trim();
									if (next !== ticket.label) onRename(ticket, next);
								}}
								onKeyDown={(event) => {
									if (event.key === "Enter")
										event.currentTarget.blur();
									if (event.key === "Escape") setEditingId(null);
								}}
								data-component="support-tab-rename"
							/>
						) : (
							<Tooltip title={ticket.title}>
								<Badge
									color="primary"
									variant="dot"
									invisible={!hasUnseen(ticket)}
									sx={{ pr: 0.5 }}
								>
									{shortLabel(ticket)}
								</Badge>
							</Tooltip>
						)
					}
				/>
			))}
			{olderCount > 0 && (
				<Tab
					value={ARCHIVE_TAB}
					icon={
						<Tooltip title={`Archived conversations (${olderCount})`}>
							<ArchiveOutlinedIcon fontSize="small" />
						</Tooltip>
					}
					sx={{ minHeight: 40, minWidth: 48 }}
					aria-label="Archived conversations"
					data-component="support-tab-history"
				/>
			)}
			<Tab
				value={NEW_MESSAGE_TAB}
				icon={<AddIcon fontSize="small" />}
				sx={{ minHeight: 40, minWidth: 48 }}
				data-component="support-tab-compose"
				aria-label="New message"
			/>
		</Tabs>
	);
};

export default SupportConversationTabs;
